/**
 * @fileoverview Pending Extraction Preview
 *
 * Reusable crop-adjustment UI for letterhead extraction. After the
 * client-side PDF render + auto-crop runs, this component shows the four
 * detected zones with trim sliders so the user can fine-tune each one
 * before uploading.
 *
 * Two modes:
 *   - **Full bundle** (default): show all four zones (Header / Footer /
 *     Left / Right) with their own slider rows. Used for new bundle
 *     uploads.
 *   - **Single zone**: limit the UI to one zone — used when the user
 *     clicks "Edit" on an existing zone in a bundle and we want to
 *     re-crop just that zone.
 */

import {
  ChevronDown,
  ChevronRight,
  Eye,
  Minus,
  Plus,
  RotateCcw,
  Check,
  Loader2,
  Maximize2,
  Minimize2,
  SlidersHorizontal,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/utils/misc";
import {
  MAX_TRIM_FRACTION,
  MAX_EXPAND_FRACTION,
  type CropTrims,
  type CropZone,
  type RenderedPage,
  type SourceBodyMargins,
  type TrimEdge,
  type ZoneSelectionFractions,
} from "@/utils/branding/extract-letterhead-images";
import {
  DEFAULT_PADDING,
  paddingAsSource,
  resolveDocumentPadding,
  variantPaddingTiers,
  type DocumentPadding,
} from "@/utils/branding/resolve-letterhead";
import {
  buildBundleFragments,
  type LetterheadAssetLike,
} from "@/utils/branding/letterheadFragments";
import { LetterheadRegionSelector } from "./LetterheadRegionSelector";
import { LetterheadSamplePreview } from "./LetterheadSamplePreview";
import { SourceBodyMarginsRow } from "./SourceBodyMarginsRow";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export interface PendingExtraction {
  sourceFile: File;
  renderedPage: RenderedPage;
  headerTrimPx: number;
  headerTopTrimPx: number;
  headerLeftTrimPx: number;
  headerRightTrimPx: number;
  footerTrimPx: number;
  footerBottomTrimPx: number;
  footerLeftTrimPx: number;
  footerRightTrimPx: number;
  leftTrimPx: number;
  rightTrimPx: number;
  leftTopTrimPx?: number;
  leftBottomTrimPx?: number;
  rightTopTrimPx?: number;
  rightBottomTrimPx?: number;
  headerBlobUrl: string | null;
  footerBlobUrl: string | null;
  leftSideBlobUrl: string | null;
  rightSideBlobUrl: string | null;
  headerImageFile: File | null;
  footerImageFile: File | null;
  leftSideImageFile: File | null;
  rightSideImageFile: File | null;
  mirrorSides: boolean;
  /**
   * Save the header as a side block that body text flows beside (the
   * engine's "topBlock" side fit) instead of a full-width header that
   * pushes the body down. For letterheads whose logo/address block sits
   * in one corner with text alongside it on the original page.
   */
  flowBeside?: boolean;
  /**
   * Keep the header/footer artwork at its position on the page ("page fit",
   * see `utils/branding/pageFit.ts`). On unless explicitly `false`; off
   * saves the historical slot fit, where the image is stretched to the
   * zone and the header/footer margins apply.
   */
  pageFit?: boolean;
  /**
   * Body side margins to save with a page-fit header/footer (px at 96dpi),
   * so the body column follows the firm's own letter — see
   * `detectSourceBodyMargins`. Null keeps the firm's margins.
   */
  bodyMargins?: SourceBodyMargins | null;
  /** What the last crop measured from the letter text (null = nothing). */
  detectedBodyMargins?: SourceBodyMargins | null;
  /** True once the user has typed their own margins (auto-detect stops following crops). */
  bodyMarginsEdited?: boolean;
  /** Where each zone was cut on the page (fractions), from the last crop. */
  selection?: ZoneSelectionFractions;
  /**
   * Zones the user switched off before saving. Their files are still
   * produced by the crop (so re-enabling is instant) but skipped on
   * confirm.
   */
  disabledZones?: Partial<Record<CropZone, boolean>>;
}

/**
 * Render a side-letterhead band against the page mock with a clear
 * crop-edge marker on the inner (page-facing) edge.
 */
export function SideBandPreview({
  src,
  alt,
  position,
  scale = 1,
}: {
  src: string;
  alt: string;
  position: "left" | "right";
  scale?: number;
}) {
  const clampedScale = Math.max(0.3, Math.min(1, scale));
  const trimPct = (1 - clampedScale) * 100;
  const clipPath =
    clampedScale < 0.999
      ? position === "right"
        ? `inset(0 0 0 ${trimPct}%)`
        : `inset(0 ${trimPct}% 0 0)`
      : undefined;
  return (
    <div
      className={cn(
        "absolute top-0 h-full",
        position === "left" ? "left-0" : "right-0",
      )}
    >
      <img
        src={src}
        alt={alt}
        className="block h-full w-auto"
        style={clipPath ? { clipPath } : undefined}
      />
      <div
        className="pointer-events-none absolute inset-y-0"
        style={{
          width: 0,
          ...(position === "right"
            ? {
                left: `${trimPct}%`,
                borderLeft: "2px dashed rgba(56, 67, 124, 0.75)",
              }
            : {
                right: `${trimPct}%`,
                borderRight: "2px dashed rgba(56, 67, 124, 0.75)",
              }),
        }}
        aria-hidden
      />
    </div>
  );
}

/**
 * Small ± icon button rendered on either side of a crop range slider.
 */
export function SliderNudgeButton({
  direction,
  onClick,
  disabled,
  title,
}: {
  direction: "down" | "up";
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  const Icon = direction === "down" ? Minus : Plus;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="flex size-5 shrink-0 items-center justify-center rounded border border-border/60 bg-background text-primary/60 transition hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon className="size-2.5" />
    </button>
  );
}

export function PendingExtractionPreview({
  pending,
  onTrimChange,
  onMirrorToggle,
  onConfirm,
  onCancel,
  uploading,
  /** When set, restrict the UI to a single zone (for per-zone Edit flow). */
  onlyZone,
  /** Override the confirm button label. */
  confirmLabel,
  /** Override the title shown in the header bar. */
  title,
  /** Resolved firm padding for the sample preview. Defaults sensibly. */
  padding,
  /** Toggle a zone on/off before saving. */
  onToggleZone,
  /** Whole-zone moves change several trims at once. */
  onTrimsChange,
  /** Toggle saving the header as a side block that text flows beside. */
  onFlowBesideToggle,
  onPageFitToggle,
  onBodyMarginsChange,
  onBodyMarginsReset,
}: {
  pending: PendingExtraction;
  onTrimChange: (zone: CropZone, edge: TrimEdge, trimPx: number) => void;
  onMirrorToggle: (next: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  uploading: boolean;
  onlyZone?: CropZone;
  confirmLabel?: string;
  title?: string;
  padding?: DocumentPadding;
  onToggleZone?: (zone: CropZone, enabled: boolean) => void;
  onTrimsChange?: (patch: Partial<CropTrims>) => void;
  onFlowBesideToggle?: (next: boolean) => void;
  onPageFitToggle?: (next: boolean) => void;
  /** The body column saved with page-fit artwork (see `SourceBodyMarginsRow`). */
  onBodyMarginsChange?: (next: SourceBodyMargins | null) => void;
  onBodyMarginsReset?: () => void;
}) {
  const totalSliderSteps = 400;
  const centerStep = totalSliderSteps / 2;
  const canvasHeight = pending.renderedPage.canvas.height;
  const maxTrimPx = Math.round(canvasHeight * MAX_TRIM_FRACTION);
  const maxExpandPx = Math.round(canvasHeight * MAX_EXPAND_FRACTION);

  const sliderToTrim = (s: number): number => {
    if (s <= centerStep) {
      return Math.round(((centerStep - s) / centerStep) * maxTrimPx);
    }
    return -Math.round(((s - centerStep) / centerStep) * maxExpandPx);
  };

  const trimToSlider = (px: number): number => {
    if (px >= 0) {
      return Math.round(centerStep - (px / maxTrimPx) * centerStep);
    }
    return Math.round(centerStep + (-px / maxExpandPx) * centerStep);
  };

  const [headerTrimSlider, setHeaderTrimSlider] = useState(
    trimToSlider(pending.headerTrimPx),
  );
  const [headerTopTrimSlider, setHeaderTopTrimSlider] = useState(
    trimToSlider(pending.headerTopTrimPx),
  );
  const [headerLeftTrimSlider, setHeaderLeftTrimSlider] = useState(
    trimToSlider(pending.headerLeftTrimPx),
  );
  const [headerRightTrimSlider, setHeaderRightTrimSlider] = useState(
    trimToSlider(pending.headerRightTrimPx),
  );
  const [footerTrimSlider, setFooterTrimSlider] = useState(
    trimToSlider(pending.footerTrimPx),
  );
  const [footerBottomTrimSlider, setFooterBottomTrimSlider] = useState(
    trimToSlider(pending.footerBottomTrimPx),
  );
  const [footerLeftTrimSlider, setFooterLeftTrimSlider] = useState(
    trimToSlider(pending.footerLeftTrimPx),
  );
  const [footerRightTrimSlider, setFooterRightTrimSlider] = useState(
    trimToSlider(pending.footerRightTrimPx),
  );
  const [leftSideTrimSlider, setLeftSideTrimSlider] = useState(
    trimToSlider(pending.leftTrimPx),
  );
  const [rightSideTrimSlider, setRightSideTrimSlider] = useState(
    trimToSlider(pending.rightTrimPx),
  );

  useEffect(() => {
    setHeaderTrimSlider(trimToSlider(pending.headerTrimPx));
    setHeaderTopTrimSlider(trimToSlider(pending.headerTopTrimPx));
    setHeaderLeftTrimSlider(trimToSlider(pending.headerLeftTrimPx));
    setHeaderRightTrimSlider(trimToSlider(pending.headerRightTrimPx));
    setFooterTrimSlider(trimToSlider(pending.footerTrimPx));
    setFooterBottomTrimSlider(trimToSlider(pending.footerBottomTrimPx));
    setFooterLeftTrimSlider(trimToSlider(pending.footerLeftTrimPx));
    setFooterRightTrimSlider(trimToSlider(pending.footerRightTrimPx));
    setLeftSideTrimSlider(trimToSlider(pending.leftTrimPx));
    setRightSideTrimSlider(trimToSlider(pending.rightTrimPx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pending.headerTrimPx,
    pending.headerTopTrimPx,
    pending.headerLeftTrimPx,
    pending.headerRightTrimPx,
    pending.footerTrimPx,
    pending.footerBottomTrimPx,
    pending.footerLeftTrimPx,
    pending.footerRightTrimPx,
    pending.leftTrimPx,
    pending.rightTrimPx,
  ]);

  const NUDGE_STEP = 1;
  const stepSlider = (
    zone: CropZone,
    edge: TrimEdge,
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
  ) => {
    setter((curr) => {
      const next = Math.max(0, Math.min(totalSliderSteps, curr + delta));
      onTrimChange(zone, edge, sliderToTrim(next));
      return next;
    });
  };

  /** Build the slider-row props for one zone edge. */
  const edgeControl = (
    zone: CropZone,
    edge: TrimEdge,
    label: string,
    slider: number,
    setSlider: React.Dispatch<React.SetStateAction<number>>,
  ): EdgeControl => ({
    label,
    slider,
    setSlider,
    centerStep,
    totalSteps: totalSliderSteps,
    onSliderChange: (s) => onTrimChange(zone, edge, sliderToTrim(s)),
    onNudge: (delta) => stepSlider(zone, edge, setSlider, delta),
    onReset: () => {
      setSlider(centerStep);
      onTrimChange(zone, edge, 0);
    },
    nudgeStep: NUDGE_STEP,
  });

  const disabledZones = pending.disabledZones ?? {};
  const showHeader = !onlyZone || onlyZone === "header";
  const showFooter = !onlyZone || onlyZone === "footer";
  const showLeftSide = !onlyZone || onlyZone === "leftSide";
  const showRightSide = !onlyZone || onlyZone === "rightSide";

  // For the single-zone re-crop flow, hide cards that have no content for
  // the targeted zone — they're not relevant to the edit. Zones switched
  // off with the toggles are hidden everywhere and skipped on save.
  const headerVisible =
    showHeader && pending.headerBlobUrl && !disabledZones.header;
  const footerVisible =
    showFooter && pending.footerBlobUrl && !disabledZones.footer;
  const leftVisible =
    showLeftSide && pending.leftSideBlobUrl && !disabledZones.leftSide;
  const rightVisible =
    showRightSide && pending.rightSideBlobUrl && !disabledZones.rightSide;
  const anyVisible = !!(
    headerVisible ||
    footerVisible ||
    leftVisible ||
    rightVisible
  );

  // Fine-tune sliders are secondary to the drag-select overlay — collapsed
  // by default in the full-bundle flow, always open in single-zone mode.
  const [fineTuneOpen, setFineTuneOpen] = useState(false);
  const slidersOpen = onlyZone ? true : fineTuneOpen;
  // Expanded = the same editor in a near-full-screen modal with a larger
  // page, for fine placement work.
  const [expanded, setExpanded] = useState(false);
  // Size the page selector + sample preview to sit side by side in whatever
  // width the host gives us (cards inline, near-full-screen when expanded).
  // Callback ref rather than an effect: when the editor is expanded, the
  // dialog portal mounts its children a tick after our render, so an effect
  // keyed on `expanded` would find the ref still empty.
  const panelsObserverRef = useRef<ResizeObserver | null>(null);
  const [panelsWidth, setPanelsWidth] = useState(0);
  const panelsRef = useCallback((el: HTMLDivElement | null) => {
    panelsObserverRef.current?.disconnect();
    panelsObserverRef.current = null;
    if (!el) return;
    setPanelsWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setPanelsWidth(el.clientWidth));
    ro.observe(el);
    panelsObserverRef.current = ro;
  }, []);
  useEffect(() => () => panelsObserverRef.current?.disconnect(), []);
  const PANEL_GAP = 32;
  const zoneWidth =
    panelsWidth > 0
      ? Math.max(
          300,
          Math.min(
            expanded ? 720 : 520,
            Math.floor((panelsWidth - PANEL_GAP - 8) / 2),
          ),
        )
      : expanded
        ? 640
        : 380;

  // Live "in place" preview: treat the cropped blobs as a throwaway bundle
  // and run them through the SAME fragment builder the exporters use.
  const previewFragments = useMemo(() => {
    const assets: LetterheadAssetLike[] = [];
    const push = (
      zoneKind: CropZone,
      url: string | null,
      off: boolean | undefined,
      extra: Partial<LetterheadAssetLike> = {},
    ) => {
      if (!url || off) return;
      assets.push({
        _id: `pending-${zoneKind}`,
        zoneKind,
        imageUrl: url,
        bundleName: "__pending__",
        ...extra,
      });
    };
    const headerSel = pending.selection?.header;
    const imageFit = pending.pageFit === false ? "slot" : "page";
    const sourceBodyMargins =
      imageFit === "page" ? (pending.bodyMargins ?? null) : null;
    if (pending.flowBeside && headerSel) {
      // Header saved as a side block: text wraps beside it, full width below.
      const side: CropZone =
        (headerSel.left + headerSel.right) / 2 < 0.5 ? "leftSide" : "rightSide";
      push(side, pending.headerBlobUrl, disabledZones.header, {
        sideFit: "topBlock",
        topBlockHeightPct: Math.max(
          0.05,
          Math.min(0.9, headerSel.bottom - headerSel.top),
        ),
        defaultPageRule: "firstOnly",
      });
    } else {
      push("header", pending.headerBlobUrl, disabledZones.header, {
        imageFit,
        sourceSelection: headerSel ?? null,
        sourceBodyMargins,
      });
    }
    push("footer", pending.footerBlobUrl, disabledZones.footer, {
      imageFit,
      sourceSelection: pending.selection?.footer ?? null,
      sourceBodyMargins,
    });
    push("leftSide", pending.leftSideBlobUrl, disabledZones.leftSide);
    push("rightSide", pending.rightSideBlobUrl, disabledZones.rightSide);
    return buildBundleFragments("__pending__", assets);
  }, [
    pending.headerBlobUrl,
    pending.footerBlobUrl,
    pending.leftSideBlobUrl,
    pending.rightSideBlobUrl,
    pending.flowBeside,
    pending.pageFit,
    pending.bodyMargins,
    pending.selection,
    disabledZones.header,
    disabledZones.footer,
    disabledZones.leftSide,
    disabledZones.rightSide,
  ]);

  // The margins the sample page renders with: the firm's, with the body
  // column this letterhead will save laid over them — the same tier the
  // exporters resolve it at.
  const inheritedPadding = padding ?? DEFAULT_PADDING;
  const previewPadding = useMemo(
    () =>
      resolveDocumentPadding(
        paddingAsSource(inheritedPadding),
        null,
        null,
        variantPaddingTiers(previewFragments),
      ),
    [inheritedPadding, previewFragments],
  );
  // Same condition as the page-fit checkbox below: a header saved as a
  // side block never page-fits, but an enabled footer still does, and its
  // saved body column must stay visible and editable.
  const pageFitOn =
    pending.pageFit !== false &&
    ((!!pending.headerBlobUrl &&
      !disabledZones.header &&
      !pending.flowBeside) ||
      (!!pending.footerBlobUrl && !disabledZones.footer));

  const zoneToggles: Array<{
    zone: CropZone;
    label: string;
    hasContent: boolean;
  }> = [
    { zone: "header", label: "Header", hasContent: !!pending.headerBlobUrl },
    { zone: "footer", label: "Footer", hasContent: !!pending.footerBlobUrl },
    {
      zone: "leftSide",
      label: "Left side",
      hasContent: !!pending.leftSideBlobUrl,
    },
    {
      zone: "rightSide",
      label: "Right side",
      hasContent: !!pending.rightSideBlobUrl,
    },
  ];

  const actions = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={uploading}
        className="rounded-md px-4 py-2 text-sm font-medium text-primary/60 transition hover:bg-secondary hover:text-primary disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={uploading || !anyVisible}
        className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </>
        ) : (
          <>
            <Check className="size-4" />
            {confirmLabel ?? "Save letterhead"}
          </>
        )}
      </button>
    </div>
  );

  const body = (
    <div className="flex flex-col gap-4">
      <div className={cn(GROUP_CARD_CLASS, "border-2 border-primary/20")}>
        <div className="flex items-center justify-between gap-3 px-6 py-3">
          <span className="text-sm font-medium text-primary/70">
            {title ?? "Select the letterhead regions before saving"}
          </span>
          <span className="flex items-center gap-3">
            <span className="text-xs text-primary/40">
              {pending.sourceFile.name}
            </span>
            {!onlyZone && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-primary/60 transition hover:bg-secondary"
                title={
                  expanded ? "Back to inline view" : "Open in a larger editor"
                }
              >
                {expanded ? (
                  <Minimize2 className="size-3" />
                ) : (
                  <Maximize2 className="size-3" />
                )}
                {expanded ? "Shrink" : "Expand"}
              </button>
            )}
          </span>
        </div>
      </div>

      {!onlyZone && (
        <div className={cn(GROUP_CARD_CLASS, "border border-primary/10 p-4")}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <div className="flex flex-wrap items-center gap-4">
              {zoneToggles.map((t) => (
                <label
                  key={t.zone}
                  className={cn(
                    "flex items-center gap-1.5 text-xs",
                    t.hasContent
                      ? "cursor-pointer text-primary/60"
                      : "text-primary/35",
                  )}
                  title={
                    t.hasContent
                      ? undefined
                      : `No ${t.label.toLowerCase()} yet — drag in from the page edge to add one`
                  }
                >
                  <input
                    type="checkbox"
                    checked={t.hasContent && !disabledZones[t.zone]}
                    disabled={!t.hasContent}
                    onChange={(e) => onToggleZone?.(t.zone, e.target.checked)}
                    className="size-3.5 cursor-pointer rounded border-border accent-primary disabled:cursor-not-allowed"
                  />
                  {t.label}
                  {!t.hasContent && (
                    <span className="text-[10px] text-primary/30">
                      (drag from page edge)
                    </span>
                  )}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {((pending.headerBlobUrl &&
                !disabledZones.header &&
                !pending.flowBeside) ||
                (pending.footerBlobUrl && !disabledZones.footer)) && (
                <label
                  className="flex cursor-pointer items-center gap-2 text-xs text-primary/60"
                  title="Draw the header and footer exactly where they sit on your letterhead, at full size. Untick to stretch them to fit inside the header and footer margins instead."
                >
                  <input
                    type="checkbox"
                    checked={pending.pageFit !== false}
                    onChange={(e) => onPageFitToggle?.(e.target.checked)}
                    className="size-3.5 cursor-pointer rounded border-border accent-primary"
                  />
                  Keep artwork at its position on the page
                </label>
              )}
              {pending.headerBlobUrl && !disabledZones.header && (
                <label
                  className="flex cursor-pointer items-center gap-2 text-xs text-primary/60"
                  title="Save the header as a block that body text flows beside (text uses the full width below it), instead of a full-width header that pushes the body down. Applies to the first page."
                >
                  <input
                    type="checkbox"
                    checked={!!pending.flowBeside}
                    onChange={(e) => onFlowBesideToggle?.(e.target.checked)}
                    className="size-3.5 cursor-pointer rounded border-border accent-primary"
                  />
                  Text flows beside the header
                </label>
              )}
              {(pending.leftSideBlobUrl || pending.rightSideBlobUrl) && (
                <label className="flex cursor-pointer items-center gap-2 text-xs text-primary/60">
                  <input
                    type="checkbox"
                    checked={pending.mirrorSides}
                    onChange={(e) => onMirrorToggle(e.target.checked)}
                    className="size-3.5 cursor-pointer rounded border-border accent-primary"
                  />
                  Mirror left &amp; right crop
                </label>
              )}
            </div>
          </div>

          {pageFitOn && onBodyMarginsChange && onBodyMarginsReset && (
            <div className="mb-3">
              <SourceBodyMarginsRow
                value={pending.bodyMargins ?? null}
                detected={pending.detectedBodyMargins ?? null}
                edited={!!pending.bodyMarginsEdited}
                inherited={inheritedPadding}
                onChange={onBodyMarginsChange}
                onReset={onBodyMarginsReset}
              />
            </div>
          )}

          <div
            ref={panelsRef}
            className="flex flex-wrap items-start justify-center gap-8"
          >
            <LetterheadRegionSelector
              page={pending.renderedPage}
              trims={pending}
              disabledZones={disabledZones}
              mirrorSides={pending.mirrorSides}
              onTrimChange={onTrimChange}
              onTrimsChange={onTrimsChange}
              width={zoneWidth}
            />
            <LetterheadSamplePreview
              fragments={previewFragments}
              padding={previewPadding}
              width={zoneWidth}
              label="Preview with sample text"
            />
          </div>
        </div>
      )}

      {!onlyZone && (
        <button
          type="button"
          onClick={() => setFineTuneOpen((o) => !o)}
          className="flex items-center gap-1.5 self-start rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary hover:text-primary/70"
        >
          {slidersOpen ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
          <SlidersHorizontal className="size-3" />
          Fine-tune with sliders
        </button>
      )}

      {slidersOpen && headerVisible && pending.headerBlobUrl && (
        <CropZoneCard
          label="Header"
          imageUrl={pending.headerBlobUrl}
          topEdge={edgeControl(
            "header",
            "top",
            "Top",
            headerTopTrimSlider,
            setHeaderTopTrimSlider,
          )}
          bottomEdge={edgeControl(
            "header",
            "bottom",
            "Bottom",
            headerTrimSlider,
            setHeaderTrimSlider,
          )}
          leftEdge={edgeControl(
            "header",
            "left",
            "Left",
            headerLeftTrimSlider,
            setHeaderLeftTrimSlider,
          )}
          rightEdge={edgeControl(
            "header",
            "right",
            "Right",
            headerRightTrimSlider,
            setHeaderRightTrimSlider,
          )}
        />
      )}

      {slidersOpen && footerVisible && pending.footerBlobUrl && (
        <CropZoneCard
          label="Footer"
          imageUrl={pending.footerBlobUrl}
          topEdge={edgeControl(
            "footer",
            "top",
            "Top",
            footerTrimSlider,
            setFooterTrimSlider,
          )}
          bottomEdge={edgeControl(
            "footer",
            "bottom",
            "Bottom",
            footerBottomTrimSlider,
            setFooterBottomTrimSlider,
          )}
          leftEdge={edgeControl(
            "footer",
            "left",
            "Left",
            footerLeftTrimSlider,
            setFooterLeftTrimSlider,
          )}
          rightEdge={edgeControl(
            "footer",
            "right",
            "Right",
            footerRightTrimSlider,
            setFooterRightTrimSlider,
          )}
        />
      )}

      {slidersOpen && (leftVisible || rightVisible) && (
        <>
          {!onlyZone && (
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-medium uppercase tracking-wider text-primary/40">
                Side panels (optional)
              </span>
            </div>
          )}

          <div className="flex flex-col gap-4">
            {leftVisible && pending.leftSideBlobUrl && (
              <SidePanelCropCard
                label="Left side"
                imageUrl={pending.leftSideBlobUrl}
                position="left"
                edge={edgeControl(
                  "leftSide",
                  "left",
                  "Crop",
                  leftSideTrimSlider,
                  setLeftSideTrimSlider,
                )}
              />
            )}
            {rightVisible && pending.rightSideBlobUrl && (
              <SidePanelCropCard
                label="Right side"
                imageUrl={pending.rightSideBlobUrl}
                position="right"
                edge={edgeControl(
                  "rightSide",
                  "right",
                  "Crop",
                  rightSideTrimSlider,
                  setRightSideTrimSlider,
                )}
              />
            )}
          </div>
        </>
      )}

      {!anyVisible && (
        <div
          className={cn(
            GROUP_CARD_CLASS,
            "border border-primary/10 px-6 py-8 text-center",
          )}
        >
          <p className="text-sm text-primary/50">
            {onlyZone
              ? `No ${onlyZone.replace(/Side$/, " side")} detected at this sensitivity. Try expanding the crop (move the slider right).`
              : "No header, footer, or side panels detected at this sensitivity."}
          </p>
        </div>
      )}
    </div>
  );

  if (expanded) {
    // Body scrolls; the Cancel / Save bar stays pinned at the bottom so it
    // can't be lost below the fold.
    return (
      <Dialog open onOpenChange={(open) => !open && setExpanded(false)}>
        <DialogContent className="flex h-[94vh] w-[96vw] max-w-[96vw] flex-col gap-0 p-0">
          <DialogTitle className="sr-only">Letterhead editor</DialogTitle>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{body}</div>
          <div className="border-t border-border bg-background px-4 py-3">
            {actions}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {body}
      {actions}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal card components — extracted for readability
// ---------------------------------------------------------------------------

/**
 * Props for one trim slider row. A zone card renders one row per editable
 * edge (header / footer have two: top + bottom; sides have one).
 */
interface EdgeControl {
  label: string;
  slider: number;
  setSlider: React.Dispatch<React.SetStateAction<number>>;
  centerStep: number;
  totalSteps: number;
  onSliderChange: (s: number) => void;
  onNudge: (delta: number) => void;
  onReset: () => void;
  nudgeStep: number;
}

/** A single labelled trim slider with ± nudge buttons and a reset control. */
function TrimSliderRow({
  edge,
  idPrefix,
}: {
  edge: EdgeControl;
  idPrefix: string;
}) {
  const sliderId = `${idPrefix}-${edge.label.toLowerCase()}-trim-slider`;
  return (
    <div className="flex items-center gap-4">
      <label
        htmlFor={sliderId}
        className="w-14 shrink-0 text-xs font-medium text-primary/60"
      >
        {edge.label}
      </label>
      <SliderNudgeButton
        direction="down"
        title="Less crop (–1)"
        onClick={() => edge.onNudge(-edge.nudgeStep)}
        disabled={edge.slider <= 0}
      />
      <input
        id={sliderId}
        type="range"
        min={0}
        max={edge.totalSteps}
        value={edge.slider}
        onChange={(e) => {
          const val = Number(e.target.value);
          edge.setSlider(val);
          edge.onSliderChange(val);
        }}
        className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-border accent-primary"
      />
      <SliderNudgeButton
        direction="up"
        title="More crop (+1)"
        onClick={() => edge.onNudge(edge.nudgeStep)}
        disabled={edge.slider >= edge.totalSteps}
      />
      <button
        type="button"
        onClick={edge.onReset}
        className={cn(
          "rounded p-1 text-primary/40 transition hover:bg-secondary hover:text-primary/70",
          edge.slider === edge.centerStep && "invisible",
        )}
        title="Reset to auto-detected crop"
      >
        <RotateCcw className="size-3" />
      </button>
    </div>
  );
}

/** A vertical trim slider that flanks the image on its left or right edge. */
function TrimSliderColumn({
  edge,
  idPrefix,
}: {
  edge: EdgeControl;
  idPrefix: string;
}) {
  const sliderId = `${idPrefix}-${edge.label.toLowerCase()}-trim-slider`;
  return (
    <div className="flex w-12 shrink-0 flex-col items-center gap-2 py-1">
      <label htmlFor={sliderId} className="text-xs font-medium text-primary/60">
        {edge.label}
      </label>
      <SliderNudgeButton
        direction="up"
        title="More crop (+1)"
        onClick={() => edge.onNudge(edge.nudgeStep)}
        disabled={edge.slider >= edge.totalSteps}
      />
      <input
        id={sliderId}
        type="range"
        min={0}
        max={edge.totalSteps}
        value={edge.slider}
        onChange={(e) => {
          const val = Number(e.target.value);
          edge.setSlider(val);
          edge.onSliderChange(val);
        }}
        style={{ writingMode: "vertical-lr" }}
        className="min-h-[80px] flex-1 cursor-pointer accent-primary"
      />
      <SliderNudgeButton
        direction="down"
        title="Less crop (–1)"
        onClick={() => edge.onNudge(-edge.nudgeStep)}
        disabled={edge.slider <= 0}
      />
      <button
        type="button"
        onClick={edge.onReset}
        className={cn(
          "rounded p-1 text-primary/40 transition hover:bg-secondary hover:text-primary/70",
          edge.slider === edge.centerStep && "invisible",
        )}
        title="Reset to auto-detected crop"
      >
        <RotateCcw className="size-3" />
      </button>
    </div>
  );
}

function CropZoneCard({
  label,
  imageUrl,
  topEdge,
  bottomEdge,
  leftEdge,
  rightEdge,
}: {
  label: string;
  imageUrl: string;
  /** Sliders framing the image — each rendered on the edge it crops. */
  topEdge?: EdgeControl;
  bottomEdge?: EdgeControl;
  leftEdge?: EdgeControl;
  rightEdge?: EdgeControl;
}) {
  const idPrefix = label.toLowerCase();
  return (
    <div className={cn(GROUP_CARD_CLASS, "border border-primary/10")}>
      <div className="flex items-center gap-2 px-6 py-3">
        <Eye className="size-3.5 text-primary/40" />
        <span className="text-sm font-medium text-primary/70">{label}</span>
      </div>
      {topEdge && (
        <div className="border-t border-border/50 px-6 py-2">
          <TrimSliderRow edge={topEdge} idPrefix={idPrefix} />
        </div>
      )}
      <div className="flex items-stretch gap-3 border-t border-border/50 bg-white px-6 py-4">
        {leftEdge && <TrimSliderColumn edge={leftEdge} idPrefix={idPrefix} />}
        <img
          src={imageUrl}
          alt={`${label} preview`}
          className="block h-auto min-w-0 flex-1 self-center"
        />
        {rightEdge && <TrimSliderColumn edge={rightEdge} idPrefix={idPrefix} />}
      </div>
      {bottomEdge && (
        <div className="border-t border-border/50 px-6 py-2">
          <TrimSliderRow edge={bottomEdge} idPrefix={idPrefix} />
        </div>
      )}
    </div>
  );
}

function SidePanelCropCard({
  label,
  imageUrl,
  position,
  edge,
}: {
  label: string;
  imageUrl: string;
  position: "left" | "right";
  edge: EdgeControl;
}) {
  return (
    <div className={cn(GROUP_CARD_CLASS, "border border-primary/10")}>
      <div className="flex items-center gap-2 px-6 py-3">
        <Eye className="size-3.5 text-primary/40" />
        <span className="text-sm font-medium text-primary/70">{label}</span>
      </div>
      <div className="flex justify-center border-t border-border/50 bg-secondary/40 px-6 py-6">
        <div
          className="relative aspect-[794/1123] h-[480px] border border-border/50 shadow-sm"
          style={{ backgroundColor: "rgb(243, 243, 247)" }}
        >
          <SideBandPreview
            src={imageUrl}
            alt={`${label} preview`}
            position={position}
          />
        </div>
      </div>
      <div className="border-t border-border/50 px-6 py-3">
        <TrimSliderRow
          edge={edge}
          idPrefix={label.toLowerCase().replace(/\s+/g, "-")}
        />
      </div>
    </div>
  );
}
